import React from 'react';
import { useTranslation } from 'react-i18next';

import { ProcessResponseDTO, ProcessResponseDTOStatus } from '@/api/generated';
import FolderCheckIcon from '@/assets/icons/folder-check.svg?react';
import FolderPencilIcon from '@/assets/icons/folder-pencil.svg?react';
import FolderShareIcon from '@/assets/icons/folder-share.svg?react';
import FolderXIcon from '@/assets/icons/folder-x.svg?react';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { useDirection } from '@/hooks/useDirection';
import { useProcessStatusActions } from '@/hooks/useProcessStatusActions';

interface ProcessActionMenuProps {
  process: ProcessResponseDTO;
  onStatusChange: (newStatus: ProcessResponseDTOStatus) => void;
}

const ProcessActionMenu = ({ process, onStatusChange }: ProcessActionMenuProps) => {
  const { t } = useTranslation(['processes', 'common']);
  const { end } = useDirection();

  const {
    publish: handlePublish,
    cancel: handleCancel,
    resume: handleResume,
    hold: handleHold,
    complete: handleComplete,
    isPublishAvailable,
    isCancelAvailable,
    isCompleteAvailable,
    isHoldAvailable,
    isResumeAvailable
  } = useProcessStatusActions({
    currentStatus: process?.status,
    onChange: onStatusChange,
    processId: process.id!
  });

  return (
    <DropdownMenu
      menuProps={{
        transformOrigin: {
          vertical: 'top',
          horizontal: end
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: end
        }
      }}
    >
      {isPublishAvailable && (
        <DropdownMenuItem
          text={t('layouts.processes.details.info.action.publish')}
          icon={<FolderShareIcon />}
          onClick={handlePublish}
        />
      )}
      {isCancelAvailable && (
        <DropdownMenuItem
          text={t('layouts.processes.details.info.action.cancel')}
          icon={<FolderXIcon />}
          onClick={handleCancel}
        />
      )}
      {isResumeAvailable && (
        <DropdownMenuItem
          text={t('layouts.processes.details.info.action.resume')}
          icon={<FolderPencilIcon />}
          onClick={handleResume}
        />
      )}
      {isHoldAvailable && (
        <DropdownMenuItem
          text={t('layouts.processes.details.info.action.hold')}
          icon={<FolderPencilIcon />}
          onClick={handleHold}
        />
      )}
      {isCompleteAvailable && (
        <DropdownMenuItem
          text={t('layouts.processes.details.info.action.complete')}
          icon={<FolderCheckIcon />}
          onClick={handleComplete}
        />
      )}
    </DropdownMenu>
  );
};

export default ProcessActionMenu;
